/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AuctionReport,
  CustomersReport,
  GetAdminV1OrgProductsIdEngagementParams,
  GetAdminV1OrgProductsIdEngagementPathParameters,
  GetAdminV1StoresStoreIdReportsAuctionsParams,
  GetAdminV1StoresStoreIdReportsAuctionsPathParameters,
  GetAdminV1StoresStoreIdReportsCustomersParams,
  GetAdminV1StoresStoreIdReportsCustomersPathParameters,
  GetAdminV1StoresStoreIdReportsExportParams,
  GetAdminV1StoresStoreIdReportsExportPathParameters,
  GetAdminV1StoresStoreIdReportsInventoryPathParameters,
  GetAdminV1StoresStoreIdReportsOverviewParams,
  GetAdminV1StoresStoreIdReportsOverviewPathParameters,
  GetAdminV1StoresStoreIdReportsPaymentsParams,
  GetAdminV1StoresStoreIdReportsPaymentsPathParameters,
  GetAdminV1StoresStoreIdReportsProductsEngagementParams,
  GetAdminV1StoresStoreIdReportsProductsEngagementPathParameters,
  GetAdminV1StoresStoreIdReportsProductsParams,
  GetAdminV1StoresStoreIdReportsProductsPathParameters,
  GetAdminV1StoresStoreIdReportsSalesParams,
  GetAdminV1StoresStoreIdReportsSalesPathParameters,
  GetSupportReportParams,
  GetSupportReportPathParameters,
  InventoryReport,
  OverviewReport,
  PaymentsReport,
  PostAdminV1StoresStoreIdReportsRollup200,
  PostAdminV1StoresStoreIdReportsRollupBody,
  PostAdminV1StoresStoreIdReportsRollupPathParameters,
  ProductEngagement,
  ProductsReport,
  SalesReport,
  StoreProductEngagementReport,
  SupportReport
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Daily counts come from the support rollup written with the sales rollup (today every 15 minutes, yesterday after midnight, POST /reports/rollup on demand); the agent, tag, missed-by-hour and SLA tables are live. An order is chat-assisted when the shopper's latest conversation got a staff reply within settings.chat.attribution.windowDays before it; refunds are subtracted in attributedNet. At most 366 days
 * @summary Chat and ticket volume, response times, ratings and chat-assisted revenue
 */
export const getSupportReport = (
    { storeId }: GetSupportReportPathParameters,
    params: GetSupportReportParams,
 options?: SecondParameter<typeof customInstance<SupportReport>>,) => {
      return customInstance<SupportReport>(
      {url: `/admin/v1/stores/${storeId}/reports/support`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Read from the daily rollups (today refreshed every 15 minutes, yesterday after midnight). `lastRollupAt` tells how fresh the numbers are; POST /reports/rollup recomputes on demand
 * @summary Totals, the previous period and a daily series
 */
export const getAdminV1StoresStoreIdReportsOverview = (
    { storeId }: GetAdminV1StoresStoreIdReportsOverviewPathParameters,
    params: GetAdminV1StoresStoreIdReportsOverviewParams,
 options?: SecondParameter<typeof customInstance<OverviewReport>>,) => {
      return customInstance<OverviewReport>(
      {url: `/admin/v1/stores/${storeId}/reports/overview`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Sales over time by day, ISO week or month
 */
export const getAdminV1StoresStoreIdReportsSales = (
    { storeId }: GetAdminV1StoresStoreIdReportsSalesPathParameters,
    params: GetAdminV1StoresStoreIdReportsSalesParams,
 options?: SecondParameter<typeof customInstance<SalesReport>>,) => {
      return customInstance<SalesReport>(
      {url: `/admin/v1/stores/${storeId}/reports/sales`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Live from order lines; canceled orders excluded, canceled quantities removed
 * @summary Best-selling variants in the range
 */
export const getAdminV1StoresStoreIdReportsProducts = (
    { storeId }: GetAdminV1StoresStoreIdReportsProductsPathParameters,
    params: GetAdminV1StoresStoreIdReportsProductsParams,
 options?: SecondParameter<typeof customInstance<ProductsReport>>,) => {
      return customInstance<ProductsReport>(
      {url: `/admin/v1/stores/${storeId}/reports/products`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Summed from the daily product rollup (today refreshed every 15 minutes, yesterday after midnight, older days never re-rolled) over store days in the store's timezone. Only products with activity in the range are listed, at most 100; revenue is order lines net of line discounts in the store currency, canceled orders excluded
 * @summary Views, add to cart, wishlist adds, orders, units and revenue per product
 */
export const getAdminV1StoresStoreIdReportsProductsEngagement = (
    { storeId }: GetAdminV1StoresStoreIdReportsProductsEngagementPathParameters,
    params: GetAdminV1StoresStoreIdReportsProductsEngagementParams,
 options?: SecondParameter<typeof customInstance<StoreProductEngagementReport>>,) => {
      return customInstance<StoreProductEngagementReport>(
      {url: `/admin/v1/stores/${storeId}/reports/products/engagement`, method: 'GET',
        params
    },
      options);
    }
  /**
 * From the daily product rollup. One entry per store the caller can access, each over its own last `days` store days ending today in that store's timezone
 * @summary One product's engagement per store over the last days
 */
export const getAdminV1OrgProductsIdEngagement = (
    { id }: GetAdminV1OrgProductsIdEngagementPathParameters,
    params?: GetAdminV1OrgProductsIdEngagementParams,
 options?: SecondParameter<typeof customInstance<ProductEngagement>>,) => {
      return customInstance<ProductEngagement>(
      {url: `/admin/v1/org/products/${id}/engagement`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary New versus returning customers and the top spenders
 */
export const getAdminV1StoresStoreIdReportsCustomers = (
    { storeId }: GetAdminV1StoresStoreIdReportsCustomersPathParameters,
    params: GetAdminV1StoresStoreIdReportsCustomersParams,
 options?: SecondParameter<typeof customInstance<CustomersReport>>,) => {
      return customInstance<CustomersReport>(
      {url: `/admin/v1/stores/${storeId}/reports/customers`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Orders by payment method, refunds and money still owed
 */
export const getAdminV1StoresStoreIdReportsPayments = (
    { storeId }: GetAdminV1StoresStoreIdReportsPaymentsPathParameters,
    params: GetAdminV1StoresStoreIdReportsPaymentsParams,
 options?: SecondParameter<typeof customInstance<PaymentsReport>>,) => {
      return customInstance<PaymentsReport>(
      {url: `/admin/v1/stores/${storeId}/reports/payments`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Stock on hand, its cost value and what is running low
 */
export const getAdminV1StoresStoreIdReportsInventory = (
    { storeId }: GetAdminV1StoresStoreIdReportsInventoryPathParameters,
 options?: SecondParameter<typeof customInstance<InventoryReport>>,) => {
      return customInstance<InventoryReport>(
      {url: `/admin/v1/stores/${storeId}/reports/inventory`, method: 'GET'
    },
      options);
    }
  /**
 * sales (by interval), products and customers (top 1000), payments and inventory (low stock, up to 1000 rows), with the same figures as the JSON reports in the store currency. from/to are required for every report but inventory
 * @summary One report as a CSV download
 */
export const getAdminV1StoresStoreIdReportsExport = (
    { storeId }: GetAdminV1StoresStoreIdReportsExportPathParameters,
    params: GetAdminV1StoresStoreIdReportsExportParams,
 options?: SecondParameter<typeof customInstance<string>>,) => {
      return customInstance<string>(
      {url: `/admin/v1/stores/${storeId}/reports/export`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Recompute the daily rollups for a range of at most 31 days
 */
export const postAdminV1StoresStoreIdReportsRollup = (
    { storeId }: PostAdminV1StoresStoreIdReportsRollupPathParameters,
    postAdminV1StoresStoreIdReportsRollupBody: BodyType<PostAdminV1StoresStoreIdReportsRollupBody>,
 options?: SecondParameter<typeof customInstance<PostAdminV1StoresStoreIdReportsRollup200>>,) => {
      return customInstance<PostAdminV1StoresStoreIdReportsRollup200>(
      {url: `/admin/v1/stores/${storeId}/reports/rollup`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdReportsRollupBody
    },
      options);
    }
  /**
 * Totals over the whole range (closed, sold, sell-through of ended auctions, revenue as hammer plus premium, bidders per auction, hammer against starting price, sales by bidding, buy now and second chance) and the closed auctions a page at a time, latest first. At most 366 days
 * @summary Auction results over a range of the store's days
 */
export const getAdminV1StoresStoreIdReportsAuctions = (
    { storeId }: GetAdminV1StoresStoreIdReportsAuctionsPathParameters,
    params: GetAdminV1StoresStoreIdReportsAuctionsParams,
 options?: SecondParameter<typeof customInstance<AuctionReport>>,) => {
      return customInstance<AuctionReport>(
      {url: `/admin/v1/stores/${storeId}/reports/auctions`, method: 'GET',
        params
    },
      options);
    }
  export type GetSupportReportResult = NonNullable<Awaited<ReturnType<typeof getSupportReport>>>
export type GetAdminV1StoresStoreIdReportsOverviewResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsOverview>>>
export type GetAdminV1StoresStoreIdReportsSalesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsSales>>>
export type GetAdminV1StoresStoreIdReportsProductsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsProducts>>>
export type GetAdminV1StoresStoreIdReportsProductsEngagementResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsProductsEngagement>>>
export type GetAdminV1OrgProductsIdEngagementResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdEngagement>>>
export type GetAdminV1StoresStoreIdReportsCustomersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsCustomers>>>
export type GetAdminV1StoresStoreIdReportsPaymentsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsPayments>>>
export type GetAdminV1StoresStoreIdReportsInventoryResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsInventory>>>
export type GetAdminV1StoresStoreIdReportsExportResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsExport>>>
export type PostAdminV1StoresStoreIdReportsRollupResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdReportsRollup>>>
export type GetAdminV1StoresStoreIdReportsAuctionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdReportsAuctions>>>
